import {
  collection,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Parse an encoded session path from a URL query parameter.
 *
 * Session path format (URI-encoded):
 *   events/{country}/{club}/{date}/{fleetClass}/{vesselId}/{sessionToken}
 *
 * Returns an object with the path components, or null if invalid.
 */
export function parseSessionPath(encoded) {
  if (!encoded) return null;
  const decoded = decodeURIComponent(encoded);
  const parts = decoded.split('/');
  // Expect: events / country / club / date / fleetClass / vesselId / sessionToken
  if (parts.length < 7 || parts[0] !== 'events') return null;
  return {
    country: parts[1],
    club: parts[2],
    date: parts[3],
    fleetClass: parts[4],
    vesselId: parts[5],
    sessionToken: parts[6],
    sessionPath: decoded,
  };
}

/**
 * Reduce a list of NavEvents into race state.
 *
 * Each event has at minimum: { type, timestamp, authority }
 * Additional fields depend on type (see nav_event.dart).
 */
export function reduceEvents(events) {
  const state = {
    status: 'waiting',       // waiting | racing | finished | abandoned
    currentMarkIndex: 0,
    legTimes: [],            // { fromMark, toMark, elapsed, timestamp }
    raceStartTime: null,
    finishTime: null,
    finishSource: null,      // 'auto' | 'crew' | 'officer'
    courseId: null,
    courseName: null,
    eventCount: 0,
    positionCount: 0,
    latestPosition: null,
  };

  for (const event of events) {
    state.eventCount++;

    switch (event.type) {
      case 'RaceStarted':
      case 'race_started':
        state.status = 'racing';
        state.raceStartTime = parseTimestamp(event.timestamp);
        state.courseId = event.course_id ?? event.courseId ?? null;
        state.courseName = event.course_name ?? event.courseName ?? null;
        state.currentMarkIndex = 0;
        break;

      case 'MarkAdvanced':
      case 'mark_advanced': {
        const toMark = event.to_mark_index ?? event.toMarkIndex;
        const fromMark = event.from_mark_index ?? event.fromMarkIndex;
        if (toMark != null) state.currentMarkIndex = toMark;
        if (state.raceStartTime && event.timestamp) {
          const ts = parseTimestamp(event.timestamp);
          const elapsed = ts - state.raceStartTime;
          state.legTimes.push({
            fromMark: fromMark ?? state.currentMarkIndex - 1,
            toMark: toMark ?? state.currentMarkIndex,
            elapsed,
            timestamp: ts,
          });
        }
        break;
      }

      case 'RaceFinished':
      case 'race_finished':
        state.status = 'finished';
        state.finishTime = parseTimestamp(event.timestamp);
        state.finishSource = event.authority ?? null;
        break;

      case 'RaceAbandoned':
      case 'race_abandoned':
        state.status = 'abandoned';
        break;

      case 'CourseChanged':
      case 'course_changed':
        state.courseId = event.new_course_id ?? event.newCourseId ?? null;
        state.courseName = event.new_course_name ?? event.newCourseName ?? null;
        break;

      case 'position_recorded':
        state.positionCount++;
        state.latestPosition = {
          timestamp: event.timestamp ?? null,
          latitude: event.latitude ?? null,
          longitude: event.longitude ?? null,
          speed: event.speed ?? null,
        };
        break;

      // WindAdjusted — informational, no state change needed for display
      default:
        break;
    }
  }

  return state;
}

/**
 * Subscribe to race events at a given device events path.
 * Returns an unsubscribe function.
 *
 * @param {string} deviceEventsPath - Full Firestore path to the events collection
 * @param {function} onEvents - Callback receiving array of event objects
 * @param {function} onError - Error callback
 */
export function subscribeToEvents(deviceEventsPath, onEvents, onError) {
  const q = query(
    collection(db, deviceEventsPath),
    orderBy('timestamp'),
  );
  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    onEvents(events);
  }, onError);
}

/** Parse a timestamp value that may be a Firestore Timestamp, Date, ISO string, or millis. */
function parseTimestamp(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate().getTime();    // Firestore Timestamp
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'string') return new Date(ts).getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

/**
 * Format elapsed milliseconds as MM:SS or HH:MM:SS.
 */
export function formatElapsed(ms) {
  if (ms == null || ms < 0) return '--:--';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}
